import { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { baseApiURL } from "../../baseUrl";
import { sortEnrollmentNo } from "../../utils/enrollmentSorter";
import { 
  FiUsers, 
  FiFilter, 
  FiCheckCircle, 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiLayers, 
  FiGrid,
  FiBookOpen
} from "react-icons/fi";

const Section = ({ branch: lockedBranch }) => {
  const [activeTab, setActiveTab] = useState("allocate"); // "manage" | "allocate"
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(lockedBranch || "-- Select --");
  const [semester, setSemester] = useState("-- Select --");

  // Allocation State
  const [fromEnrollment, setFromEnrollment] = useState("");
  const [toEnrollment, setToEnrollment] = useState("");
  const [selectedSection, setSelectedSection] = useState("-- Select --");
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);

  // Dynamic Section Management State
  const [availableSections, setAvailableSections] = useState([]);
  const [sectionsList, setSectionsList] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [newSectionCapacity, setNewSectionCapacity] = useState(60);
  const [editingSection, setEditingSection] = useState(null);
  const [editSectionName, setEditSectionName] = useState("");
  const [editSectionCapacity, setEditSectionCapacity] = useState(60);

  // Custom sorting function for enrollment numbers
  const sortEnrollmentNumbers = (studentsList, order = 'ascending') => {
    const sorted = [...studentsList].sort(sortEnrollmentNo);
    return order === 'ascending' ? sorted : sorted.reverse();
  };

  // Fetch branches and students
  const fetchData = async () => {
    try {
      const branchRes = await axios.get(`${baseApiURL()}/branch/getBranch`);
      if (branchRes.data.success) setBranches(branchRes.data.branches);

      const studentRes = await axios.get(`${baseApiURL()}/student/details/getDetails2`);
      if (studentRes.data.success) {
        const sortedStudents = sortEnrollmentNumbers(studentRes.data.students, 'ascending');
        setAllStudents(sortedStudents);
      }
    } catch (error) {
      toast.error("Error fetching data");
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch sections list for Management view and Dropdowns
  const fetchSections = async () => {
    try {
      let url = `${baseApiURL()}/section/getSections`;
      let params = {};
      if (selectedBranch && selectedBranch !== "-- Select --") params.branch = selectedBranch;
      if (semester && semester !== "-- Select --") params.semester = semester;

      const res = await axios.get(url, { params });
      if (res.data.success) {
        setSectionsList(res.data.sections);
      }

      // Fetch dynamic dropdown options
      const dropdownRes = await axios.get(`${baseApiURL()}/section/getSectionsByBranchAndSemester`, { params });
      if (dropdownRes.data.success) {
        setAvailableSections(dropdownRes.data.sections);
      }
    } catch (error) {
      console.error("Error fetching dynamic sections:", error);
    }
  };

  useEffect(() => {
    fetchSections();
  }, [selectedBranch, semester]);

  // Filter students by selected filters
  useEffect(() => {
    let result = allStudents;

    if (selectedBranch !== "-- Select --") {
      result = result.filter((s) => s.branch === selectedBranch);
    }

    if (semester !== "-- Select --") {
      result = result.filter((s) => String(s.semester) === String(semester));
    }

    if (fromEnrollment) {
      result = result.filter((s) => s.enrollmentNo >= fromEnrollment);
    }

    if (toEnrollment) {
      result = result.filter((s) => s.enrollmentNo <= toEnrollment);
    }

    const sortedResult = sortEnrollmentNumbers(result, 'ascending');
    setFilteredStudents(sortedResult);
    setSelectedStudents([]);
  }, [selectedBranch, semester, fromEnrollment, toEnrollment, allStudents]);

  // Student Selection Handlers
  const handleStudentSelect = (enrollmentNo) => {
    setSelectedStudents(prev => 
      prev.includes(enrollmentNo)
        ? prev.filter(id => id !== enrollmentNo)
        : [...prev, enrollmentNo]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.enrollmentNo));
    }
  };

  // Section Assignment Action
  const handleAssignSection = async () => {
    if (
      selectedBranch === "-- Select --" ||
      semester === "-- Select --" ||
      selectedSection === "-- Select --"
    ) {
      toast.error("Branch, Semester, and Section are required");
      return;
    }

    if (selectedStudents.length === 0) {
      toast.error("Please select at least one student");
      return;
    }

    try {
      const res = await axios.put(`${baseApiURL()}/student/details/assignSection`, {
        branch: selectedBranch,
        semester,
        section: selectedSection,
        studentEnrollments: selectedStudents,
      });

      if (res.data.success) {
        toast.success(res.data.message);
        await fetchData();
        await fetchSections();
        setSelectedStudents([]);
      } else {
        toast.error(res.data.message || "Failed to assign section");
      }
    } catch (error) {
      toast.error("Error while assigning section");
      console.error(error);
    }
  };

  // Dynamic Section CRUD Actions
  const handleAddSection = async (e) => {
    e.preventDefault();
    if (!newSectionName.trim()) {
      toast.error("Please enter a section name");
      return;
    }
    if (selectedBranch === "-- Select --" || semester === "-- Select --") {
      toast.error("Please select Branch and Semester first");
      return;
    }

    try {
      const res = await axios.post(`${baseApiURL()}/section/addSection`, {
        name: newSectionName.trim(),
        branch: selectedBranch,
        semester: Number(semester),
        capacity: Number(newSectionCapacity),
      });

      if (res.data.success) {
        toast.success(res.data.message);
        setNewSectionName("");
        setIsAddModalOpen(false);
        fetchSections();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add section");
    }
  };

  const handleUpdateSection = async (e) => {
    e.preventDefault();
    if (!editSectionName.trim()) {
      toast.error("Please enter a section name");
      return;
    }

    try {
      const res = await axios.put(`${baseApiURL()}/section/updateSection/${editingSection._id}`, {
        name: editSectionName.trim(),
        capacity: Number(editSectionCapacity),
      });

      if (res.data.success) {
        toast.success("Section updated successfully!");
        setIsEditModalOpen(false);
        setEditingSection(null);
        fetchSections();
        fetchData();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update section");
    }
  };

  const handleDeleteSection = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete Section "${name}"? Students in this section will be unassigned.`)) {
      return;
    }

    try {
      const res = await axios.delete(`${baseApiURL()}/section/deleteSection/${id}`);
      if (res.data.success) {
        toast.success(res.data.message);
        fetchSections();
        fetchData();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error("Error deleting section");
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
            <FiLayers className="text-indigo-600 dark:text-indigo-400" />
            Dynamic Section Management
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Define dynamic sections per Branch and Semester, rename sections, and assign students.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex bg-slate-100 dark:bg-slate-900 p-1 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs self-start sm:self-auto">
          <button
            onClick={() => setActiveTab("allocate")}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              activeTab === "allocate"
                ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200 dark:border-slate-700"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <FiUsers className="w-4 h-4" />
            <span>Student Allocation</span>
          </button>

          <button
            onClick={() => setActiveTab("manage")}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              activeTab === "manage"
                ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200 dark:border-slate-700"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <FiGrid className="w-4 h-4" />
            <span>Manage Sections</span>
          </button>
        </div>
      </div>

      {/* Global Branch & Semester Filters */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-5 space-y-4">
        <div className="flex items-center space-x-2">
          <FiFilter className="text-indigo-600 dark:text-indigo-400 text-base" />
          <h2 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Select Cohort (Branch & Semester)</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Branch</label>
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              disabled={!!lockedBranch}
              className={`w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white ${
                lockedBranch ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <option>-- Select --</option>
              {branches.map((b) => (
                <option key={b.name} value={b.name}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Semester</label>
            <select
              value={semester}
              onChange={(e) => setSemester(e.target.value)}
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
            >
              <option>-- Select --</option>
              {[...Array(8).keys()].map((i) => (
                <option key={i + 1} value={i + 1}>
                  Semester {i + 1}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                if (selectedBranch === "-- Select --" || semester === "-- Select --") {
                  toast.error("Please select both Branch and Semester first");
                  return;
                }
                setIsAddModalOpen(true);
              }}
              className="w-full py-2 px-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl transition shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <FiPlus className="w-4 h-4" />
              <span>Add Dynamic Section</span>
            </button>
          </div>
        </div>
      </div>

      {/* TAB 1: MANAGE SECTIONS */}
      {activeTab === "manage" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <FiBookOpen className="text-indigo-600 dark:text-indigo-400" />
              Configured Sections ({sectionsList.length})
            </h3>
            {selectedBranch !== "-- Select --" && semester !== "-- Select --" && (
              <span className="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 font-bold text-[11px] rounded-lg border border-indigo-200 dark:border-indigo-800">
                {selectedBranch} - Sem {semester}
              </span>
            )}
          </div>

          {sectionsList.length === 0 ? (
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-12 text-center text-slate-400">
              <FiGrid className="w-12 h-12 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
              <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-0.5">No Custom Sections Found</h3>
              <p className="text-xs text-slate-500 mb-4">
                No dynamic sections created for this cohort yet. Default sections (A, B, C, D) are currently used.
              </p>
              <button
                onClick={() => {
                  if (selectedBranch === "-- Select --" || semester === "-- Select --") {
                    toast.error("Please select Branch and Semester first");
                    return;
                  }
                  setIsAddModalOpen(true);
                }}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl transition inline-flex items-center gap-1.5 cursor-pointer shadow-xs"
              >
                <FiPlus className="w-4 h-4" />
                <span>Create Section Now</span>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {sectionsList.map((sec) => (
                <div key={sec._id} className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-5 flex flex-col justify-between hover:border-indigo-500/50 transition">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xl font-black text-slate-900 dark:text-white tracking-wide">
                        {sec.name}
                      </span>
                      <span className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 text-[10px] font-bold rounded-md border border-indigo-200 dark:border-indigo-800">
                        Cap: {sec.capacity}
                      </span>
                    </div>

                    <div className="text-xs text-slate-500 dark:text-slate-400 space-y-1 mb-3">
                      <div><strong className="text-slate-700 dark:text-slate-300">Branch:</strong> {sec.branch}</div>
                      <div><strong className="text-slate-700 dark:text-slate-300">Semester:</strong> {sec.semester}</div>
                      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                        <FiUsers className="text-indigo-600 dark:text-indigo-400" />
                        <span className="text-xs font-bold text-slate-900 dark:text-white">
                          {sec.studentCount} Student{sec.studentCount !== 1 ? 's' : ''} Enrolled
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                    <button
                      onClick={() => {
                        setEditingSection(sec);
                        setEditSectionName(sec.name);
                        setEditSectionCapacity(sec.capacity);
                        setIsEditModalOpen(true);
                      }}
                      className="p-1.5 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-slate-800 rounded-lg transition flex items-center gap-1 text-xs font-bold cursor-pointer"
                      title="Rename / Edit Section"
                    >
                      <FiEdit2 className="w-3.5 h-3.5" />
                      <span>Rename</span>
                    </button>

                    <button
                      onClick={() => handleDeleteSection(sec._id, sec.name)}
                      className="p-1.5 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/50 rounded-lg transition flex items-center gap-1 text-xs font-bold cursor-pointer"
                      title="Delete Section"
                    >
                      <FiTrash2 className="w-3.5 h-3.5" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB 2: STUDENT ALLOCATION */}
      {activeTab === "allocate" && (
        <div className="space-y-6">
          {/* Allocation Controls Card */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Select Section</label>
                <select
                  value={selectedSection}
                  onChange={(e) => setSelectedSection(e.target.value)}
                  className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
                >
                  <option>-- Select --</option>
                  {availableSections.map((sec) => (
                    <option key={sec} value={sec}>
                      Section {sec}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">From Enrollment No</label>
                <input
                  type="text"
                  value={fromEnrollment}
                  onChange={(e) => setFromEnrollment(e.target.value)}
                  className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
                  placeholder="e.g. 22N81A0501"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">To Enrollment No</label>
                <input
                  type="text"
                  value={toEnrollment}
                  onChange={(e) => setToEnrollment(e.target.value)}
                  className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
                  placeholder="e.g. 22N81A05B9"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={handleAssignSection}
                  className="w-full py-2 px-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl transition shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <FiCheckCircle className="w-4 h-4" />
                  <span>Assign Section</span>
                </button>
              </div>
            </div>
          </div>

          {/* Student List Table */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FiUsers className="text-indigo-600 dark:text-indigo-400 text-base" />
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">Filtered Students</h3>
              </div>
              <div className="flex items-center space-x-3 text-xs text-slate-500 dark:text-slate-400 font-semibold">
                <span>{selectedStudents.length} of {filteredStudents.length} selected</span>
                <span>•</span>
                <span>{filteredStudents.length} total found</span>
              </div>
            </div>

            <div>
              {filteredStudents.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <FiUsers className="w-12 h-12 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                  <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300">No Students Found</h3>
                  <p className="text-xs text-slate-500 mt-0.5">No students match the selected Branch and Semester criteria.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left divide-y divide-slate-100 dark:divide-slate-800">
                    <thead className="bg-slate-50 dark:bg-slate-950 text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      <tr>
                        <th className="px-5 py-3">
                          <input
                            type="checkbox"
                            checked={selectedStudents.length === filteredStudents.length && filteredStudents.length > 0}
                            onChange={handleSelectAll}
                            className="rounded text-indigo-600 focus:ring-indigo-500"
                          />
                        </th>
                        <th className="px-5 py-3">Enrollment No</th>
                        <th className="px-5 py-3">Student Name</th>
                        <th className="px-5 py-3">Email</th>
                        <th className="px-5 py-3 text-center">Semester</th>
                        <th className="px-5 py-3">Branch</th>
                        <th className="px-5 py-3 text-center">Current Section</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                      {filteredStudents.map((s) => (
                        <tr key={s.enrollmentNo} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <td className="px-5 py-3">
                            <input
                              type="checkbox"
                              checked={selectedStudents.includes(s.enrollmentNo)}
                              onChange={() => handleStudentSelect(s.enrollmentNo)}
                              className="rounded text-indigo-600 focus:ring-indigo-500"
                            />
                          </td>
                          <td className="px-5 py-3 font-mono font-bold text-indigo-600 dark:text-indigo-400">{s.enrollmentNo}</td>
                          <td className="px-5 py-3 font-medium text-slate-900 dark:text-white">{`${s.firstName} ${s.middleName || ""} ${s.lastName}`}</td>
                          <td className="px-5 py-3 text-slate-500 dark:text-slate-400">{s.email}</td>
                          <td className="px-5 py-3 text-center">
                            <span className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 rounded-lg text-[10px] font-bold">
                              Sem {s.semester}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-slate-800 dark:text-slate-200">{s.branch}</td>
                          <td className="px-5 py-3 text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                              s.section 
                                ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800' 
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                            }`}>
                              {s.section ? `Sec ${s.section}` : "Unassigned"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ADD SECTION MODAL */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/75 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-200 dark:border-slate-800 space-y-4">
            <h3 className="text-base font-bold text-slate-900 dark:text-white">Add New Dynamic Section</h3>
            <form onSubmit={handleAddSection} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Branch</label>
                <input
                  type="text"
                  disabled
                  value={selectedBranch}
                  className="w-full p-2 text-xs bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-500 dark:text-slate-400 font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Semester</label>
                <input
                  type="text"
                  disabled
                  value={`Semester ${semester}`}
                  className="w-full p-2 text-xs bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-500 dark:text-slate-400 font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Section Name / Code</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. A, B, C, CSE-1, WIPRO TRAINING"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  className="w-full p-2 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Section Capacity</label>
                <input
                  type="number"
                  required
                  min="1"
                  value={newSectionCapacity}
                  onChange={(e) => setNewSectionCapacity(e.target.value)}
                  className="w-full p-2 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-bold text-xs rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
                >
                  Create Section
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT SECTION MODAL */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/75 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-200 dark:border-slate-800 space-y-4">
            <h3 className="text-base font-bold text-slate-900 dark:text-white">Rename / Edit Section</h3>
            <form onSubmit={handleUpdateSection} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Section Name</label>
                <input
                  type="text"
                  required
                  value={editSectionName}
                  onChange={(e) => setEditSectionName(e.target.value)}
                  className="w-full p-2 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white"
                />
                <p className="text-[11px] text-indigo-600 dark:text-indigo-400 mt-1">
                  Note: Renaming this section will automatically update all students currently assigned to "{editingSection?.name}".
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Section Capacity</label>
                <input
                  type="number"
                  required
                  min="1"
                  value={editSectionCapacity}
                  onChange={(e) => setEditSectionCapacity(e.target.value)}
                  className="w-full p-2 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-bold text-xs rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Section;